import {
  CalendarDate,
  endOfMonth,
  getLocalTimeZone,
  startOfMonth,
  today,
} from "@internationalized/date";
import DateRangePicker from "components/datePickers/DateRangePicker";
import { PsychologistDropdown } from "components/lib/PsychologistDropdown";
import { addDays, differenceInDays, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { usePsychologistUser } from "../../hooks";
import { useUserHasRole } from "../../hooks/userHooks";
import { CancelledSlotsDashboardGridItem } from "./CancelledSlotsDashboardGridItem";
import IcbtDashboardGridItem from "./IcbtDashboardGridItem";
import { PatientsWithoutFutureBookings } from "./PatientsWithoutFutureBookings";
import { TimeslotsDashboardGridItem } from "./TimeslotsDashboardGridItem";

export const PsychologistDashboard = () => {
  const { t } = useTranslation();
  const isAdmin = useUserHasRole("Admin");
  const { psychologist } = usePsychologistUser();

  const [selectedPsychologistUserId] = useState(psychologist?.user?.userId);

  const [dateRangePickerValue, setDateRangePickerValue] = useState<{
    start: CalendarDate;
    end: CalendarDate;
  }>({
    start: startOfMonth(today(getLocalTimeZone())),
    end: endOfMonth(today(getLocalTimeZone())),
  });

  const fromDate = dateRangePickerValue.start.toDate(getLocalTimeZone());

  // Adding one day to include also the end date in the query
  const toDate = addDays(
    dateRangePickerValue.end.toDate(getLocalTimeZone()),
    1,
  );

  const dateRangeLengthInDays = differenceInDays(toDate, fromDate);

  const [previousFromDate, setPreviousFromDate] = useState<Date>(
    subDays(fromDate, dateRangeLengthInDays),
  );
  const [previousToDate, setPreviousToDate] = useState<Date>(
    subDays(toDate, dateRangeLengthInDays),
  );

  // @TODO: This useEffect feels like it shouldn't even exist. Should be removed.
  // However, watch out for rerenders when removing it.
  useEffect(() => {
    const newFromDate = dateRangePickerValue.start.toDate(getLocalTimeZone());

    // Adding one day to include also the end date in the query
    const newToDate = addDays(
      dateRangePickerValue.end.toDate(getLocalTimeZone()),
      1,
    );

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreviousFromDate(subDays(newFromDate, dateRangeLengthInDays));

    setPreviousToDate(subDays(newToDate, dateRangeLengthInDays));
  }, [
    dateRangeLengthInDays,
    dateRangePickerValue.end,
    dateRangePickerValue.start,
  ]);

  return (
    <PageWrapper>
      <PageContentWrapper>
        <HeaderWrapper>
          <div>
            <Typography variant="h1">{t("DASHBOARD")}</Typography>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Typography variant="body" color="GRAY_DARK">
              All data is live and updated in real time every time the page is
              refreshed.
            </Typography>
          </div>
          <HeaderSettingsWrapper>
            {isAdmin && <PsychologistDropdown />}
            <DateRangePicker
              label={"Date range"}
              value={dateRangePickerValue}
              onChange={(val) => {
                if (val) {
                  setDateRangePickerValue({ end: val.end, start: val.start });
                }
              }}
              granularity="day"
              shouldForceLeadingZeros
            />
          </HeaderSettingsWrapper>
        </HeaderWrapper>
        <DashboardGrid>
          <TimeslotsDashboardGridItem
            psychologistUserId={selectedPsychologistUserId}
            fromDate={fromDate}
            toDate={toDate}
            previousFromDate={previousFromDate}
            previousToDate={previousToDate}
            dateRangeLengthInDays={dateRangeLengthInDays}
          />
          <CancelledSlotsDashboardGridItem
            psychologistUserId={selectedPsychologistUserId}
            fromDate={fromDate}
            toDate={toDate}
            previousFromDate={previousFromDate}
            previousToDate={previousToDate}
            dateRangeLengthInDays={dateRangeLengthInDays}
          />
          <IcbtDashboardGridItem
            psychologistUserId={selectedPsychologistUserId}
            fromDate={fromDate}
            toDate={toDate}
            previousFromDate={previousFromDate}
            previousToDate={previousToDate}
            dateRangeLengthInDays={dateRangeLengthInDays}
          />
          <PatientsWithoutFutureBookings
            psychologistUserId={selectedPsychologistUserId}
            fromDate={fromDate}
            toDate={toDate}
            previousFromDate={previousFromDate}
            previousToDate={previousToDate}
            dateRangeLengthInDays={dateRangeLengthInDays}
          />
        </DashboardGrid>
      </PageContentWrapper>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  padding-inline: ${({ theme }) => theme.SPACING.XL};
  padding-block: ${({ theme }) => theme.SPACING.SM};
`;

const PageContentWrapper = styled.div`
  width: min(100%, 1200px);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.SPACING.LG};
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.SPACING.MD};
`;

const HeaderSettingsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.SPACING.MD};
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.SPACING.LG};

  @media (max-width: ${({ theme }) => theme.BREAKPOINTS.LG}) {
    grid-template-columns: 1fr;
  }
`;
